import React from "react";
import { Form, Formik, setNestedObjectValues } from "formik";
import * as Yup from "yup";
import {
    Box, Grid, TextField, Typography, Autocomplete, Button,
    FormControlLabel, Checkbox,
} from "@mui/material";
import CustomDatePicker from "../../components/form/CustomDatePicker";
import { RISK_LEVEL } from "../../utils/constants";
import moment from "moment";
import { styles } from "../../utils/styles";
import { set } from "lodash";

const DRAFT_KEY = 'onboarding_draft';

const loadStepData = (stepIndex) => {
    try {
        const raw = window.localStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        return data.allFormData ? data.allFormData[stepIndex] : null;
    } catch (e) {
        console.error('Failed to load step data from storage:', e);
        return null;
    }
};

// Validation Schema for Step 4 KYC
const validationSchema = Yup.object().shape({
    merchant_category_code: Yup.string().required("MCC is required"),
    last_kyc_review: Yup.date().required("Last KYC Review is required").max(new Date(), "Last KYC Review date cannot be in the future"),
    risk_level: Yup.string().required("Risk Level is required"),
    next_kyc_review: Yup.date().nullable(),
    remarks: Yup.string().nullable(),
    approval_checkbox: Yup.boolean().oneOf([true], "You must confirm the checks"),
});

const Step4KYC = ({ formData, onBack, onNext, onSkip, isSubmitting, activeBusinessEntity, businessEntityName, hideCheckbox, readOnly }) => {

    // Initial values
    const today = moment();
    // console.log('formData', formData);
    const loadedData = loadStepData(3);
    const initialValues = formData || loadedData || {
        merchant_category_code: "",
        last_kyc_review: today.toDate(),
        risk_level: "high",
        next_kyc_review: today.clone().add(90, 'days').toDate(),
        remarks: "",
        approval_checkbox: false,
    };
    // Ensure approval_checkbox is always boolean to prevent controlled/uncontrolled switch
    initialValues.approval_checkbox = initialValues.approval_checkbox ?? false;

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={() => {}}
            validateOnMount={true}
            validateOnChange={true}
            validateOnBlur={true}
            enableReinitialize={true}
        >
            {({ values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue, validateForm, setTouched, isSubmitting: formikSubmitting }) => (
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <Grid container spacing={4} sx={{ py: 2, px: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="h6" my={1}>
                                KYC Information
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={3}>
                                    <Typography align="right">Merchant Category Code (MCC)*</Typography>
                                </Grid>
                                <Grid item xs={12} sm={9}>
                                    <Autocomplete
                                        options={activeBusinessEntity?.mcc?.map((mcc) => ({
                                            id: mcc.id,
                                            label: mcc.code_description,
                                        })) || []}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.id}>
                                                {option.label}
                                            </li>
                                        )}
                                        value={activeBusinessEntity?.mcc?.find(mcc => mcc.id === Number(values.merchant_category_code)) ? {
                                            id: activeBusinessEntity.mcc.find(mcc => mcc.id === Number(values.merchant_category_code)).id,
                                            label: activeBusinessEntity.mcc.find(mcc => mcc.id === Number(values.merchant_category_code)).code_description
                                        } : null}
                                        onChange={(e, newValue) => {
                                            setFieldValue("merchant_category_code", newValue?.id ?? "");
                                        }}
                                        fullWidth
                                        disabled={isSubmitting || readOnly}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Merchant Category Code (MCC)"
                                                error={Boolean(
                                                    touched.merchant_category_code &&
                                                    errors.merchant_category_code
                                                )}
                                                helperText={touched.merchant_category_code && errors.merchant_category_code}
                                                margin="dense"
                                                size="small"
                                                name="merchant_category_code"
                                                required={true}
                                            />
                                        )}
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value?.id
                                        }
                                    />
                                </Grid>

                                {/* Last KYC Reviewed Date */}
                                <Grid item xs={12} sm={3}>
                                    <Typography align="right">Last KYC Reviewed*</Typography>
                                </Grid>

                                <Grid item xs={12} sm={9}>
                                    <CustomDatePicker
                                        label="Last KYC Reviewed Date"
                                        value={values.last_kyc_review ? moment(values.last_kyc_review) : null}
                                        onChange={(date) => {
                                            setFieldValue("last_kyc_review", date ? date.toDate() : null);
                                            // Auto-calculate next KYC review date
                                            if (values.risk_level) {
                                                const lastDate = date || moment();
                                                const days = values.risk_level === 'high' ? 90 : values.risk_level === 'mid' ? 365 : 730;
                                                const nextDate = moment(lastDate).add(days, 'days');
                                                setFieldValue("next_kyc_review", nextDate.toDate());
                                            }
                                        }}
                                        disabled={isSubmitting || readOnly}
                                        disableFuture={true}
                                        maxDate={moment()}
                                        error={Boolean(touched.last_kyc_review && errors.last_kyc_review)}
                                        helperText={touched.last_kyc_review && errors.last_kyc_review}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                margin: "dense",
                                                fullWidth: true,
                                                required: true,
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* Risk Level */}
                                <Grid item xs={12} sm={3}>
                                    <Typography align="right">Risk Level*</Typography>
                                </Grid>
                                <Grid item xs={12} sm={9}>
                                    <Autocomplete
                                        options={RISK_LEVEL}
                                        getOptionLabel={(option) => option.label}
                                        value={RISK_LEVEL.find(option => option.id === values.risk_level) || null}
                                        onChange={(event, newValue) => {
                                            setFieldValue("risk_level", newValue ? newValue.id : "");
                                            // Auto-calculate next KYC review date
                                            if (newValue) {
                                                const lastDate = values.last_kyc_review || moment();
                                                const days = newValue.id === 'high' ? 90 : newValue.id === 'mid' ? 365 : 730;
                                                const nextDate = moment(lastDate).add(days, 'days');
                                                setFieldValue("next_kyc_review", nextDate.toDate());
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Risk Level"
                                                size="small"
                                                margin="dense"
                                                error={Boolean(touched.risk_level && errors.risk_level)}
                                                helperText={touched.risk_level && errors.risk_level}
                                                disabled={isSubmitting || readOnly}
                                                required={true}
                                            />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                    />
                                </Grid>

                                {/* Next KYC Review Date */}
                                <Grid item xs={12} sm={3}>
                                    <Typography align="right">Next KYC Review</Typography>
                                </Grid>
                                <Grid item xs={12} sm={9}>
                                    <CustomDatePicker
                                        label="Next KYC Review Date"
                                        value={values.next_kyc_review ? moment(values.next_kyc_review) : null}
                                        onChange={(date) => setFieldValue("next_kyc_review", date ? date.toDate() : null)}
                                        disabled={isSubmitting || readOnly}
                                        error={Boolean(touched.next_kyc_review && errors.next_kyc_review)}
                                        helperText={touched.next_kyc_review && errors.next_kyc_review}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                margin: "dense",
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* Remarks */}
                                <Grid item xs={12} sm={3}>
                                    <Typography align="right">Remarks</Typography>
                                </Grid>
                                <Grid item xs={12} sm={9}>
                                    <TextField
                                        label="Remarks"
                                        margin="dense"
                                        fullWidth
                                        multiline
                                        rows={4}
                                        id="remarks"
                                        name="remarks"
                                        size="small"
                                        color="secondary"
                                        value={values.remarks}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={isSubmitting || readOnly}
                                        error={Boolean(touched.remarks && errors.remarks)}
                                        helperText={touched.remarks && errors.remarks}
                                    />
                                </Grid>

                                {/* Approval Checkbox */}
                                {!hideCheckbox && (
                                    <>
                                        <Grid item xs={12} sm={3}>
                                            <Typography align="right"></Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={9}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="approval_checkbox"
                                                        checked={values.approval_checkbox}
                                                        onChange={handleChange}
                                                        disabled={isSubmitting || readOnly}
                                                    />
                                                }
                                                label={`I have completed the necessary checks and support the onboarding of ${businessEntityName || 'the business entity'}`}
                                            />
                                            {touched.approval_checkbox && errors.approval_checkbox && (
                                                <Typography variant="caption" color="error" display="block">
                                                    {errors.approval_checkbox}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </>
                                )}
                                {/* Buttons */}
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, position: 'relative', zIndex: 11 }}>
                                        <Button onClick={onBack} variant="outlined" disabled={isSubmitting || readOnly}>
                                            Back
                                        </Button>
                                        {!readOnly && (
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <Button
                                                    type="button"
                                                    variant="outlined"
                                                    onClick={async () => {
                                                        const formErrors = await validateForm();
                                                        if (Object.keys(formErrors).length > 0) {
                                                            setTouched(setNestedObjectValues(formErrors, true));
                                                            return;
                                                        }
                                                        onNext('reject', values);
                                                    }}
                                                    disabled={isSubmitting}
                                                >
                                                    Reject
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    onClick={async () => {
                                                        const formErrors = await validateForm();
                                                        if (Object.keys(formErrors).length > 0) {
                                                            setTouched(setNestedObjectValues(formErrors, true));
                                                            return;
                                                        }
                                                        onNext('approve', values);
                                                    }}
                                                    disabled={isSubmitting}
                                                    sx={{ ...styles.customButton }}
                                                >
                                                    {isSubmitting ? 'Approving...' : 'Approve'}
                                                </Button>
                                            </Box>
                                        )}

                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>

                    </Grid>
                </Form>
            )}
        </Formik>
    );
};

export default Step4KYC;